import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { SITE, absoluteUrl, buildStructuredData } from "../config/site";
import { formatEditionLongDate } from "../lib/contentDate";

const JSON_LD_ID = "daily-lingo-json-ld";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Injects JSON-LD structured data for Google Search and AI crawlers that execute JS. */
export default function SeoHead() {
  const { dateKey } = useParams();

  useEffect(() => {
    const isEditionPage = Boolean(dateKey);
    const pageUrl = isEditionPage ? absoluteUrl(`/edition/${dateKey}`) : absoluteUrl("/");
    const editionLabel = isEditionPage ? formatEditionLongDate(dateKey) : null;
    const title = isEditionPage
      ? `The Daily Lingo — ${editionLabel} Edition`
      : SITE.title;
    const description = isEditionPage
      ? `Read ${editionLabel}'s news articles translated into your target language, with hover English hints and vocabulary games from that day's headlines.`
      : SITE.description;

    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", SITE.keywords);
    upsertLink("canonical", pageUrl);

    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);

    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    let script = document.getElementById(JSON_LD_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = JSON_LD_ID;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildStructuredData());

    return () => {
      script?.remove();
    };
  }, [dateKey]);

  return null;
}
